import { isAuthMethod } from 'apps/cdp-web-demo/app/data/authMethods';
import { CONFIG_TO_QUERY_MAP } from 'apps/cdp-web-demo/app/data/queryParams';
import {
  AccountCreationType,
  ConfigState,
  isAccentColor,
  isAccountCreationType,
  isBorderRadius,
  isFontFamily,
  isHexColor,
  isNonEmptyArray,
} from 'apps/cdp-web-demo/app/types';

/**
 * Parse query string into a ConfigState object
 */
export function parseQueryParams(queryString: string): Partial<ConfigState> | null {
  if (!queryString) {
    return null;
  }

  try {
    const searchParams = new URLSearchParams(queryString);

    // Check if there are any query params at all
    if (!searchParams.toString()) {
      return null;
    }

    const parsed: Partial<ConfigState> = {};
    const config: Partial<ConfigState['config']> = {};

    // Parse theme mode
    const themeMode = searchParams.get(CONFIG_TO_QUERY_MAP.themeMode);
    if (themeMode && (themeMode === 'light' || themeMode === 'dark')) {
      parsed.themeMode = themeMode;
    }

    // Parse accent color
    const accentColor = searchParams.get(CONFIG_TO_QUERY_MAP.accentColor);
    if (accentColor && isAccentColor(accentColor)) {
      parsed.accentColor = accentColor;
      if (isHexColor(accentColor)) {
        parsed.customColor = accentColor;
      }
    }

    // Parse border radius
    const borderRadius = searchParams.get(CONFIG_TO_QUERY_MAP.borderRadius);
    if (borderRadius && isBorderRadius(borderRadius)) {
      parsed.borderRadius = borderRadius;
    }

    // Parse font family
    const fontFamily = searchParams.get(CONFIG_TO_QUERY_MAP.fontFamily);
    if (fontFamily && isFontFamily(fontFamily)) {
      parsed.fontFamily = fontFamily;
    }

    // Parse app name
    const appName = searchParams.get(CONFIG_TO_QUERY_MAP.appName);
    if (appName) {
      config.appName = appName;
    }

    // Parse app logo URL
    const appLogoUrl = searchParams.get(CONFIG_TO_QUERY_MAP.appLogoUrl);
    if (appLogoUrl) {
      config.appLogoUrl = appLogoUrl;
    }

    // Parse auth methods (filter to only AuthMethodBase values: 'email' | 'sms')
    const authMethods = searchParams.get(CONFIG_TO_QUERY_MAP.authMethods);
    if (authMethods) {
      const methods = authMethods.split(',').filter(isAuthMethod);
      if (methods.length > 0) {
        config.authMethods = isNonEmptyArray(methods) ? methods : undefined;
      }
    }

    // Parse show Coinbase footer
    const showCoinbaseFooter = searchParams.get(CONFIG_TO_QUERY_MAP.showCoinbaseFooter);
    if (showCoinbaseFooter) {
      config.showCoinbaseFooter = showCoinbaseFooter === 'true';
    }

    // Parse account creation type
    const accountCreationType = searchParams.get(CONFIG_TO_QUERY_MAP.accountCreationType);
    if (accountCreationType && isAccountCreationType(accountCreationType)) {
      const eth =
        accountCreationType === 'evm-smart' || accountCreationType === 'evm-eoa'
          ? ({
              createOnLogin: accountCreationType.split('-')[1] as 'smart' | 'eoa',
            } as const)
          : undefined;
      const sol =
        accountCreationType === 'solana'
          ? ({
              createOnLogin: accountCreationType === 'solana',
            } as const)
          : undefined;
      config.ethereum = eth;
      config.solana = sol;
    }

    // Only include config if it has properties
    if (Object.keys(config).length > 0) {
      parsed.config = config;
    }

    return Object.keys(parsed).length > 0 ? parsed : null;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('Error parsing query params', error);
    }
    return null;
  }
}

/**
 * Get account creation type from config state
 */
function getAccountCreationType(config?: ConfigState['config']): AccountCreationType | undefined {
  if (!config) {
    return undefined;
  }

  if (config.ethereum?.createOnLogin === 'smart') {
    return 'evm-smart';
  }
  if (config.ethereum?.createOnLogin === 'eoa') {
    return 'evm-eoa';
  }
  if (config.solana?.createOnLogin) {
    return 'solana';
  }

  return undefined;
}

/**
 * Serialize config state to individual query params
 */
export function serializeConfigToQueryParams(state: Partial<ConfigState>): URLSearchParams {
  const params = new URLSearchParams();

  // Add theme mode
  if (state.themeMode) {
    params.set(CONFIG_TO_QUERY_MAP.themeMode, state.themeMode);
  }

  // Add accent color (customColor not needed, will be derived from accent if hex)
  if (state.accentColor) {
    params.set(CONFIG_TO_QUERY_MAP.accentColor, state.accentColor);
  }

  // Add border radius
  if (state.borderRadius) {
    params.set(CONFIG_TO_QUERY_MAP.borderRadius, state.borderRadius);
  }

  // Add font family
  if (state.fontFamily) {
    params.set(CONFIG_TO_QUERY_MAP.fontFamily, state.fontFamily);
  }

  // Add config properties
  if (state.config) {
    if (state.config.appName) {
      params.set(CONFIG_TO_QUERY_MAP.appName, state.config.appName);
    }

    if (state.config.appLogoUrl) {
      params.set(CONFIG_TO_QUERY_MAP.appLogoUrl, state.config.appLogoUrl);
    }

    if (state.config.authMethods?.length) {
      params.set(CONFIG_TO_QUERY_MAP.authMethods, state.config.authMethods.join(','));
    }

    if (state.config.showCoinbaseFooter !== undefined) {
      params.set(CONFIG_TO_QUERY_MAP.showCoinbaseFooter, String(state.config.showCoinbaseFooter));
    }

    // Add account creation type
    const accountCreationType = getAccountCreationType(state.config);
    if (accountCreationType) {
      params.set(CONFIG_TO_QUERY_MAP.accountCreationType, accountCreationType);
    }
  }

  return params;
}
